import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { Typography } from '@kizen/kds/Typography';
import { Button } from '@kizen/kds/Button';
import KizenAILogo from 'pages/Marketplace/assets/kizen-ai.svg?react';
import { ChatSessionHistoryItem } from '../types';

type SessionHistoryItemProps = {
  session: ChatSessionHistoryItem;
  isActive: boolean;
  onClick: () => void;
  onDelete: () => void;
};

export const SessionHistoryItem: FC<SessionHistoryItemProps> = ({
  session,
  isActive,
  onClick,
  onDelete,
}) => {
  const { t } = useTranslation();

  const isReadOnly =
    session.status === 'complete' || session.status === 'canceled';

  const truncatedPreview = useMemo(() => {
    const preview = session.user_output || t('No messages yet');
    return preview.length > 60 ? `${preview.substring(0, 60)}...` : preview;
  }, [session.user_output, t]);

  const formattedDate = useMemo(() => {
    try {
      return formatDistanceToNow(new Date(session.last_message_datetime), {
        addSuffix: true,
      });
    } catch {
      return '';
    }
  }, [session.last_message_datetime]);

  return (
    <div
      className={`flex items-start gap-[10px] px-4 py-3 pt-[5px] cursor-pointer border-bottom border-border-medium-light
                  hover:bg-gray-100 min-h-[64px] group ${isActive ? 'bg-blue-50' : ''}`}
      onClick={onClick}
      data-qa-id={`session-history-item-${session.id}`}
    >
      <div className="flex w-[20px] h-[20px] mt-[5px] mr-[5px] flex-shrink-0 items-center">
        <KizenAILogo className="w-full h-full" />
      </div>
      <div className="flex flex-col min-w-0 gap-[10px] max-w-[350px]">
        <Typography size="md" weight="semibold" color="ai-system-text" truncate>
          {truncatedPreview}
        </Typography>
        <Typography size="sm" color="font-secondary">
          {formattedDate}
        </Typography>
      </div>
      <div className="flex items-center gap-[10px] ml-auto">
        {isReadOnly && (
          <span className="px-3 py-1 rounded-[20px] border border-border-default bg-background-white">
            <Typography size="sm" weight="regular" color="font-primary">
              {t('Closed')}
            </Typography>
          </span>
        )}
        <Button
          variant="text"
          color="tertiary"
          rightIcon="action-delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          rightIconSettings={{
            tooltipEnabled: false,
          }}
          data-qa-id={`delete-session-${session.id}`}
        />
      </div>
    </div>
  );
};
